import { queryOptions } from '@tanstack/react-query';

import type {
  AdminControllerGetLogsData,
  AppUserDto,
  AppUsersPageDto,
  AuditLogsPageDto,
  CardSideDto,
  CardTemplateDto,
  CreateTemplateDto,
  CreateUserDto,
  DashboardDto,
  UpdateTemplateDto,
  UpdateUserDto,
} from './generated';
import {
  adminControllerCreateUser,
  adminControllerGetLogs,
  adminControllerGetUsers,
  adminControllerUpdateUser,
  cardSideControllerCreateSide,
  cardSideControllerDeleteSide,
  cardSideControllerGetSides,
  cardTemplateControllerCreateTemplate,
  cardTemplateControllerDeleteTemplate,
  cardTemplateControllerGetAllTemplates,
  cardTemplateControllerGetTemplateSvg,
  cardTemplateControllerUpdateTemplate,
  dashboardControllerGet,
} from './generated';

// ─── AppUser ────────────────────────────────────────────────────────────────

export type AppUser = AppUserDto;

export type AppUsersPage = AppUsersPageDto;

export const appUserSortFields = [
  'displayName',
  'upn',
  'role',
  'active',
  'createdAt',
  'updatedAt',
] as const;
export type AppUserSortField = (typeof appUserSortFields)[number];

export type AdminUsersParams = {
  q?: string;
  sortBy?: AppUserSortField;
  sortDir?: 'asc' | 'desc';
  page?: number;
  limit?: number;
};

export const adminUsersQueryOptions = (params: AdminUsersParams = {}) =>
  queryOptions({
    queryKey: ['admin', 'users', params] as const,
    queryFn: async () => {
      const { data } = await adminControllerGetUsers({
        query: params,
        throwOnError: true,
      });
      return data;
    },
    staleTime: 30_000,
  });

export async function createAppUser(dto: CreateUserDto): Promise<AppUser> {
  const { data } = await adminControllerCreateUser({
    body: dto,
    throwOnError: true,
  });
  return data;
}

/**
 * The page with `user` swapped in where it already sits — same order,
 * everything else untouched. Pages that don't hold the user come back as the
 * same reference, so their subscribers don't re-render.
 */
export function withUpdatedUser(
  page: AppUsersPage,
  user: AppUser,
): AppUsersPage {
  if (!page.users.some((u) => u.oid === user.oid)) {
    return page;
  }
  return {
    ...page,
    users: page.users.map((u) => (u.oid === user.oid ? user : u)),
  };
}

export async function updateAppUser(
  oid: string,
  dto: UpdateUserDto,
): Promise<AppUser> {
  const { data } = await adminControllerUpdateUser({
    path: { oid },
    body: dto,
    throwOnError: true,
  });
  return data;
}

// ─── AuditLog ────────────────────────────────────────────────────────────────

export type LogsPage = AuditLogsPageDto;
export type AuditLog = LogsPage['logs'][number];

type LogsParams = AdminControllerGetLogsData['query'];

export const logsQueryOptions = (params?: LogsParams) =>
  queryOptions({
    queryKey: ['admin', 'logs', params] as const,
    queryFn: async () => {
      const { data } = await adminControllerGetLogs({
        query: params,
        throwOnError: true,
      });
      return data;
    },
    staleTime: 30_000,
  });

// ─── Dashboard ───────────────────────────────────────────────────────────────

export type Dashboard = DashboardDto;

export const dashboardQueryOptions = () =>
  queryOptions({
    queryKey: ['admin', 'dashboard'] as const,
    queryFn: async () => {
      const start = performance.now();
      const { data } = await dashboardControllerGet({ throwOnError: true });
      const latencyMs = Math.round(performance.now() - start);
      return { ...data, latencyMs };
    },
    refetchInterval: 10_000,
  });

// ─── CardSide ────────────────────────────────────────────────────────────────

export type CardSide = CardSideDto;

export const sidesQueryOptions = () =>
  queryOptions({
    queryKey: ['admin', 'sides'] as const,
    queryFn: async () => {
      const { data } = await cardSideControllerGetSides({ throwOnError: true });
      return data;
    },
    staleTime: 60_000,
  });

export async function uploadSide(
  name: string,
  svgFile: File,
): Promise<CardSide> {
  const { data } = await cardSideControllerCreateSide({
    body: { svg: svgFile, name },
    throwOnError: true,
  });
  return data;
}

export async function deleteSide(id: string): Promise<{ deleted: boolean }> {
  const { data } = await cardSideControllerDeleteSide({
    path: { id },
    throwOnError: true,
  });
  return data;
}

// ─── CardTemplate ────────────────────────────────────────────────────────────

export type CardTemplate = CardTemplateDto;

export const templatesQueryOptions = () =>
  queryOptions({
    queryKey: ['admin', 'templates'] as const,
    queryFn: async () => {
      const { data } = await cardTemplateControllerGetAllTemplates({
        throwOnError: true,
      });
      return data;
    },
    staleTime: 60_000,
  });

export const templateQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['admin', 'templates', id] as const,
    queryFn: async () => {
      const { data } = await cardTemplateControllerGetTemplateSvg({
        path: { id },
        throwOnError: true,
      });
      return data;
    },
    staleTime: 60_000,
  });

export async function createTemplate(
  dto: CreateTemplateDto,
): Promise<CardTemplate> {
  const { data } = await cardTemplateControllerCreateTemplate({
    body: dto,
    throwOnError: true,
  });
  return data;
}

export async function updateTemplate(
  id: string,
  dto: UpdateTemplateDto,
): Promise<CardTemplate> {
  const { data } = await cardTemplateControllerUpdateTemplate({
    path: { id },
    body: dto,
    throwOnError: true,
  });
  return data;
}

export async function deleteTemplate(
  id: string,
): Promise<{ deleted: boolean }> {
  const { data } = await cardTemplateControllerDeleteTemplate({
    path: { id },
    throwOnError: true,
  });
  return data;
}
